/**
 * Iter123 — Client Request Center.
 *
 * Two views depending on role:
 *  - Clients see their own requests + a "Submit new request" form
 *  - Admins (main_hub/department_admin) see the full triage queue
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { Mailbox, Send, RefreshCw, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const CATEGORIES = ['NEW_INVESTIGATION', 'STATUS_UPDATE', 'ADDITIONAL_INFORMATION', 'URGENT_ASSISTANCE', 'OTHER'];
const URGENCY = ['LOW', 'NORMAL', 'HIGH', 'CRITICAL'];
const dt = (v) => v ? new Date(v).toLocaleString() : '—';

export default function ClientRequestCenter() {
  const { user } = useAuth();
  const isAdmin = ['main_hub', 'department_admin'].includes(user?.role);
  const token = localStorage.getItem('nexus_token');
  const H = { Authorization: `Bearer ${token}` };
  const JH = { ...H, 'Content-Type': 'application/json' };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [caseIdField, setCaseIdField] = useState('');

  const [f, setF] = useState({
    title: '', summary: '', category: 'NEW_INVESTIGATION', urgency: 'NORMAL',
  });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = isAdmin ? `${BACKEND_URL}/api/client-requests` : `${BACKEND_URL}/api/client-requests/mine`;
      const r = await fetch(url, { headers: H });
      setRows(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [isAdmin, token]);
  useEffect(() => { load(); }, [load]);

  const submitRequest = async () => {
    if (f.title.trim().length < 3 || f.summary.trim().length < 10) {
      return toast({ title: 'Please add more detail', variant: 'destructive' });
    }
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/client-requests`, { method: 'POST', headers: JH, body: JSON.stringify(f) });
      const d = await r.json();
      if (!r.ok) return toast({ title: 'Submission failed', description: d.detail, variant: 'destructive' });
      toast({ title: 'Request submitted', description: 'An investigator will follow up shortly.' });
      setF({ title: '', summary: '', category: 'NEW_INVESTIGATION', urgency: 'NORMAL' });
      await load();
    } finally { setBusy(false); }
  };

  const review = async (action) => {
    if (!selected) return;
    const r = await fetch(`${BACKEND_URL}/api/client-requests/${selected.id}/review`, {
      method: 'POST', headers: JH, body: JSON.stringify({ action, notes, case_id: caseIdField }),
    });
    if (!r.ok) return toast({ title: 'Failed', variant: 'destructive' });
    toast({ title: `Request ${action.toLowerCase()}` });
    setSelected(null); setNotes(''); setCaseIdField('');
    await load();
  };

  return (
    <div className="space-y-6" data-testid="client-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-blue-500/20 border border-blue-500/40 flex items-center justify-center">
            <Mailbox className="w-6 h-6 text-blue-300" />
          </div>
          <div>
            <div className="text-xs text-blue-300 uppercase tracking-wider font-mono">{isAdmin ? 'Admin · Triage' : 'Client Portal'}</div>
            <h1 className="text-2xl font-bold text-white">Client Request Center</h1>
            <p className="text-slate-400 text-sm">{isAdmin ? 'All incoming client requests.' : 'Open a new request or check your existing submissions.'}</p>
          </div>
        </div>
        <Button variant="ghost" onClick={load} className="text-slate-300"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
      </div>

      {!isAdmin && (
        <Card className="bg-[#131A2A] border-[#334155]">
          <CardHeader><CardTitle className="text-white text-base">New Request</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={f.title} onChange={e => set('title', e.target.value)} placeholder="Short title" className="bg-slate-800 border-slate-600 text-white" data-testid="request-title" />
              <Select value={f.category} onValueChange={v => set('category', v)}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="request-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={f.urgency} onValueChange={v => set('urgency', v)}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="request-urgency"><SelectValue /></SelectTrigger>
                <SelectContent>{URGENCY.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea value={f.summary} onChange={e => set('summary', e.target.value)}
              rows={4} placeholder="Describe your request in detail…"
              className="bg-slate-800 border-slate-600 text-white" data-testid="request-summary" />
            <div className="flex justify-end">
              <Button className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" onClick={submitRequest} disabled={busy} data-testid="request-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Submit
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-[#131A2A] border-[#334155] lg:col-span-1">
          <CardHeader><CardTitle className="text-sm text-white">Requests ({rows.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-[70vh] overflow-y-auto">
            {rows.length === 0 ? <div className="text-xs text-slate-500 text-center py-6">No requests.</div> : rows.map(r => (
              <button key={r.id} onClick={() => { setSelected(r); setNotes(r.review_notes || ''); setCaseIdField(r.case_id || ''); }}
                className={`w-full text-left p-2 rounded border ${selected?.id === r.id ? 'bg-blue-900/40 border-blue-500' : 'bg-slate-900 border-slate-700 hover:border-slate-600'}`}
                data-testid={`request-row-${r.id}`}>
                <div className="text-sm text-white truncate">{r.title}</div>
                <div className="text-[10px] text-slate-400">{r.category?.replace(/_/g, ' ')} · {r.urgency}</div>
                <div className="text-[10px] text-slate-500 flex items-center justify-between mt-1">
                  <span>{dt(r.submitted_at)}</span>
                  <Badge className={`text-[9px] ${r.status === 'NEW' ? 'bg-emerald-500/20 text-emerald-300' : r.status === 'PROMOTED' ? 'bg-blue-500/20 text-blue-300' : r.status === 'DECLINED' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>{r.status}</Badge>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-[#131A2A] border-[#334155] lg:col-span-2">
          <CardHeader><CardTitle className="text-sm text-white">Request Detail</CardTitle></CardHeader>
          <CardContent>
            {!selected ? <div className="text-sm text-slate-500 text-center py-8">Select a request.</div> : (
              <div className="space-y-3 text-sm text-slate-200">
                <div><b>Title:</b> {selected.title}</div>
                <div className="text-xs text-slate-400"><b>Client:</b> {selected.client_name} · <b>Submitted:</b> {dt(selected.submitted_at)}</div>
                <div className="bg-slate-900/60 border border-slate-700 rounded p-2 whitespace-pre-wrap">{selected.summary}</div>
                {isAdmin && selected.status === 'NEW' && (
                  <>
                    <Input value={caseIdField} onChange={e => setCaseIdField(e.target.value)}
                      placeholder="Optional case ID to attach" className="bg-slate-800 border-slate-700 text-white" data-testid="request-case-id" />
                    <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                      placeholder="Review notes" className="bg-slate-800 border-slate-700 text-white" data-testid="request-notes-input" />
                    <div className="flex gap-2">
                      <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => review('PROMOTE')} data-testid="request-promote-btn">
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Promote to Case
                      </Button>
                      <Button variant="outline" className="border-amber-500/40 !text-amber-300" onClick={() => review('REVIEW')} data-testid="request-review-btn">Mark Reviewing</Button>
                      <Button className="bg-red-600 hover:bg-red-500 text-white" onClick={() => review('DECLINE')} data-testid="request-decline-btn">
                        <XCircle className="w-4 h-4 mr-1" /> Decline
                      </Button>
                    </div>
                  </>
                )}
                {selected.review_notes && <div className="text-xs text-slate-400"><b>Review notes:</b> {selected.review_notes}</div>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
